import { idbGet, idbSet } from '../lib/idb';
import { fetchWithin } from '../lib/net';

// The latest pictures and news from the observatories whose work is free to share (CC BY 4.0:
// ESA/Webb, ESA/Hubble, ESO, NOIRLab), and NASA's Astronomy Picture of the Day. Fetched straight
// from their RSS feeds (all allow it), kept for six hours, and shown as text: nothing runs from them.

export interface NewsItem {
  source: string;
  title: string;
  link: string;
  date: number;
  summary: string;
  image?: string;
}

interface Feed { source: string; url: string; kind: 'picture' | 'news' }

const FEEDS: Feed[] = [
  { source: 'ESA/Webb', url: 'https://esawebb.org/images/potm/feed/', kind: 'picture' },
  { source: 'ESA/Hubble', url: 'https://esahubble.org/images/potw/feed/', kind: 'picture' },
  { source: 'ESO', url: 'https://www.eso.org/public/images/potw/feed/', kind: 'picture' },
  { source: 'NOIRLab', url: 'https://noirlab.edu/public/images/iotw/feed/', kind: 'picture' },
  { source: 'ESA/Webb', url: 'https://esawebb.org/news/feed/', kind: 'news' },
  { source: 'ESA/Hubble', url: 'https://esahubble.org/news/feed/', kind: 'news' },
  { source: 'ESO', url: 'https://www.eso.org/public/news/feed/', kind: 'news' },
  { source: 'NOIRLab', url: 'https://noirlab.edu/public/news/feed/', kind: 'news' },
];

const HOSTS = /^https:\/\/([\w-]+\.)*(esawebb\.org|esahubble\.org|eso\.org|noirlab\.edu|nasa\.gov)\//;
/** Only the observatories' own pages, always over https (NOIRLab's feeds still say http). */
const safe = (u: string | null | undefined) => {
  const v = u?.trim().replace(/^http:\/\//, 'https://');
  return v && HOSTS.test(v) ? v : undefined;
};

/** Plain text from a feed's HTML-escaped description (no markup is ever inserted into the page). */
function textOf(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

export function parseFeed(xml: string, source: string): NewsItem[] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  return [...doc.querySelectorAll('item')].flatMap((it) => {
    const link = safe(it.querySelector('link')?.textContent?.trim());
    const title = it.querySelector('title')?.textContent?.trim();
    if (!link || !title) return [];
    const enc = it.querySelector('enclosure')?.getAttribute('url') ?? undefined;
    // The feeds point at full-screen images; their 700 px versions are a tenth of the size.
    const image = safe(enc?.replace('/images/screen/', '/images/thumb700x/'));
    const summary = textOf(it.querySelector('description')?.textContent ?? '');
    return [{ source, title, link, date: Date.parse(it.querySelector('pubDate')?.textContent ?? '') || 0, summary, image }];
  });
}

export interface Apod { title: string; date: string; credit: string; link: string; image?: string }

/** Picture credits made only of agencies whose images are free to show (anyone else's may be copyright). */
const AGENCY = /^(NASA|ESA|CSA|ESO|NOIRLab|NSF|AURA|STScI|JPL|Caltech|JPL-Caltech|GSFC|SDO|SOHO|JAXA|Webb|Hubble|ESA\/Webb|ESA\/Hubble)$/i;
export const openCredit = (credit: string) => {
  const parts = credit.split(/\s*(?:,|;|&|\band\b|\/)\s*/).map((x) => x.trim()).filter(Boolean);
  return parts.length > 0 && parts.every((x) => AGENCY.test(x));
};

async function loadApod(): Promise<Apod | null> {
  type Post = { title?: string; date?: string; copyright?: string; credit?: string; url?: string; permalink?: string; hdurl?: string; media_type?: string };
  const j = await fetchWithin('https://science.nasa.gov/wp-json/wp/v2/apod-basic?per_page=1', (r) => (r.ok ? (r.json() as Promise<Post[]>) : null));
  const a = j?.[0];
  if (!a?.title) return null;
  const credit = textOf(a.copyright || a.credit || '');
  const link = safe(a.permalink) ?? safe(a.url) ?? 'https://science.nasa.gov/apod/';
  const image = a.media_type === 'image' && openCredit(credit) ? safe(a.hdurl?.replace(/\?.*$/, '?w=900&fit=clip')) : undefined;
  return { title: textOf(a.title), date: a.date ?? '', credit, link, image };
}

export interface NewsBundle { pictures: NewsItem[]; news: NewsItem[]; apod: Apod | null; fetched: number }

/** Words that say a picture or story is about people (the owner's item 17): those are left out. */
const PEOPLE = /\b(astronauts?|cosmonauts?|crews?|portraits?|people|persons?|man|men|woman|women|girls?|boys?|child|children|famil(?:y|ies)|visitors?|selfies?)\b/i;
export const aboutPeople = (s: string) => PEOPLE.test(s);
/** A bundle without what is about people, whenever it was fetched. */
const clean = (b: NewsBundle): NewsBundle => ({
  ...b,
  pictures: b.pictures.filter((p) => !aboutPeople(`${p.title} ${p.summary}`)),
  news: b.news.filter((n) => !aboutPeople(`${n.title} ${n.summary}`)),
  apod: b.apod && !aboutPeople(b.apod.title) ? b.apod : null,
});

let inflight: Promise<NewsBundle | null> | null = null;
/** Everything for the Discover section; cached for six hours, and an older copy is better than none. */
export function loadNews(): Promise<NewsBundle | null> {
  return (inflight ??= (async () => {
    const cached = await idbGet<NewsBundle>('news');
    if (cached && Date.now() - cached.fetched < 6 * 3600e3) return clean(cached);
    // Each source on its own: one that stalls is left out, and the rest still come.
    const apod = loadApod().catch(() => null);
    const results = await Promise.all(
      FEEDS.map((f) =>
        fetchWithin(f.url, (r) => (r.ok ? r.text() : ''))
          .then((xml) => ({ f, items: xml ? parseFeed(xml, f.source) : [] }))
          .catch(() => ({ f, items: [] as NewsItem[] })),
      ),
    );
    const pick = (kind: Feed['kind'], n: number) =>
      results.filter((r) => r.f.kind === kind).flatMap((r) => r.items).filter((x) => !aboutPeople(`${x.title} ${x.summary}`)).sort((a, b) => b.date - a.date).slice(0, n);
    const bundle: NewsBundle = { pictures: pick('picture', 12), news: pick('news', 10), apod: await apod, fetched: Date.now() };
    if (!bundle.pictures.length && !bundle.news.length) return cached ? clean(cached) : null;
    await idbSet('news', bundle);
    return clean(bundle);
  })().finally(() => (inflight = null)));
}
